import { dayLabel, todayIso } from './trackerFormat';

/**
 * The dashboard's time ranges.
 *
 * Kyu, 2026-08-27: "range the metrics that have real dates behind them / keep
 * pipeline-stock figures as 'now' / label the distinction clearly so the
 * numbers stay honest."
 *
 * This module is only half of that instruction — the half that says WHICH
 * DAYS. What may legitimately be ranged is decided in dashboardSections.ts,
 * and the short version of that decision belongs here too, because anybody
 * adding a range to a new figure will read this file first:
 *
 *   A lead's row records when it ARRIVED and when it was last touched. It
 *   does not record when it entered a status. `lead_stage_log` does log stage
 *   moves, but it stores the status's DISPLAY NAME at the time of the move,
 *   across a vocabulary rename — Cold / Active Deal / Won before 20261122,
 *   New / Converted / Closed after — with no key to join on. Deriving "moved
 *   into In Talks this week" from that means matching a string somebody can
 *   edit in the UI, which is the bug class stageKeys.ts exists to delete.
 *
 * So: arrivals, touches, replies, meetings and conversions can be ranged,
 * because each has an exact date. Pipeline counts cannot, and are labelled
 * "now" instead of being windowed on a date they do not have.
 *
 * ── Two conventions, both deliberate ───────────────────────────────────────
 *
 * WEEKS START MONDAY. Everything here is UTC, matching activityDayIso — an
 * aggregate that mixed UTC for some rows and browser-local for others would
 * double-count the evening either side of midnight. Kyu's framing for this
 * page is Monday-morning, and a Sunday-start week files Monday's work under
 * the previous week.
 *
 * THE END IS NEVER IN THE FUTURE. "This Month" on the 3rd means the 1st to
 * the 3rd, not the 1st to the 31st. A window whose end has not happened yet
 * is a window over data that cannot exist, and it makes every rate computed
 * inside it quietly wrong.
 */

export type RangeKey = 'week' | 'lastWeek' | 'month' | 'quarter' | 'year';

/** The order they appear in the control: shortest first. */
export const RANGE_ORDER: RangeKey[] = ['week', 'lastWeek', 'month', 'quarter', 'year'];

export const RANGE_LABELS: Record<RangeKey, string> = {
  week: 'This Week',
  lastWeek: 'Last Week',
  month: 'This Month',
  quarter: 'This Quarter',
  year: 'This Year',
};

/** Kyu's default. The page opens on the week it is in. */
export const DEFAULT_RANGE: RangeKey = 'week';

export interface DateRange {
  /** ISO day, inclusive. */
  from: string;
  /** ISO day, inclusive. Never later than `today`. */
  to: string;
}

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The Monday on or before `iso`. */
export function mondayOf(iso: string): string {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  // getUTCDay is 0 for Sunday. Sunday belongs to the week that started six
  // days earlier, not to the one starting tomorrow.
  return addDays(iso, -((day + 6) % 7));
}

export function rangeBounds(key: RangeKey, today: string = todayIso()): DateRange {
  if (key === 'week') {
    return { from: mondayOf(today), to: today };
  }
  if (key === 'lastWeek') {
    // The only range that both starts AND ends in the past. Its end is a real
    // Sunday rather than today, so it stays still while the week goes on.
    const from = addDays(mondayOf(today), -7);
    return { from, to: addDays(from, 6) };
  }
  if (key === 'month') {
    return { from: `${today.slice(0, 7)}-01`, to: today };
  }
  if (key === 'quarter') {
    const month = Number(today.slice(5, 7)) - 1;
    const first = Math.floor(month / 3) * 3 + 1;
    return { from: `${today.slice(0, 4)}-${String(first).padStart(2, '0')}-01`, to: today };
  }
  return { from: `${today.slice(0, 4)}-01-01`, to: today };
}

/**
 * The actual days a range covers, spelled out.
 *
 * Shown next to the label rather than instead of it. "This Month" is what you
 * picked; "1 Aug – 27 Aug" is what you are looking at, and on the 3rd of the
 * month those are different enough to matter.
 */
export function rangeSpanLabel(range: DateRange): string {
  // dayLabel, not shortDate: these days came out of UTC arithmetic and must be
  // printed in the zone they were computed in, or the label contradicts the
  // filter that produced it. See trackerFormat.dayLabel.
  return range.from === range.to
    ? dayLabel(range.from)
    : `${dayLabel(range.from)} – ${dayLabel(range.to)}`;
}

/** Whether an ISO day or timestamp falls inside the range. */
export function inRange(iso: string, range: DateRange): boolean {
  const day = iso.slice(0, 10);
  return day >= range.from && day <= range.to;
}
